import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";
import {
  FaCalendarAlt,
  FaCheck,
  FaCheckSquare,
  FaClock,
  FaHandHoldingUsd,
  FaListAlt,
  FaSquare,
  FaSyncAlt,
  FaTimes,
  FaTrashAlt,
  FaUserGraduate,
} from "react-icons/fa";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import SelectSessionClass from "../ChildComponent/SelectSessionClass";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const PAGE_LIMIT = 30;

const STATUS = {
  PENDING: "1",
  SETTLED: "2",
  REJECTED: "3",
};

const TABS = [
  { value: "all", label: "All", color: "#0288d1", icon: FaListAlt },
  { value: STATUS.PENDING, label: "Pending", color: "#f59e0b", icon: FaClock },
  { value: STATUS.SETTLED, label: "Settled", color: "#10b981", icon: FaCheck },
  {
    value: STATUS.REJECTED,
    label: "Rejected",
    color: "#ef4444",
    icon: FaTimes,
  },
];

//Created By Cursor
const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
};

//Created By Cursor
const studentRelation = (gender) => {
  const g = String(gender || "").toLowerCase();
  if (g.startsWith("f") || g === "female" || g === "girl") return "D/O";
  return "S/O";
};

//Created By Cursor
const studentInitials = (name) => {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return (parts[0]?.[0] || "?").toUpperCase();
};

//Created By Cursor
const hasStudentImage = (path) => {
  const p = String(path || "").trim();
  if (!p) return false;
  const lower = p.toLowerCase();
  return !(
    lower === "null" ||
    lower === "undefined" ||
    lower.endsWith("/null") ||
    lower.includes("noimage") ||
    lower.includes("no_image") ||
    lower.includes("default.jpg") ||
    lower.includes("placeholder")
  );
};

//Created By Cursor
const parseStudentDetails = (raw) => {
  if (!raw) return {};
  if (Array.isArray(raw)) return raw[0] || {};
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed[0] || {};
      return parsed || {};
    } catch (e) {
      return {};
    }
  }
  return {};
};

//Created By Cursor
const statusLabel = (status) => {
  if (String(status) === STATUS.SETTLED) return "Settled";
  if (String(status) === STATUS.REJECTED) return "Rejected";
  return "Pending";
};

//Created By Cursor
const getRowKey = (item, index = 0) =>
  [
    item?.settlement_request_id || "req",
    item?.student_id || "stu",
    item?.fee_id || "fee",
    item?.applicable_month || "m",
    item?.due_amount ?? "",
    index,
  ].join("|");

//Created By Cursor
const StudentThumb = ({ name, imagePath }) => {
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [imagePath]);

  const showImg = hasStudentImage(imagePath) && !imgFailed;
  const letter = studentInitials(name);

  return (
    <div className="vsl-card__avatar-wrap" aria-hidden>
      {showImg ? (
        <img
          src={imagePath}
          alt=""
          className="stl-card__avatar"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span className="vsl-card__initials" title={name || "Student"}>
          {letter}
        </span>
      )}
    </div>
  );
};

const ViewSettlement = () => {
  const token = localStorage.getItem("token");
  const [loggedIn] = useState(token !== null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionStatus, setSessionStatus] = useState("");
  const [classId, setClassId] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [classSectionName, setClassSectionName] = useState("");
  const [selectedClassPosition, setSelectedClassPosition] = useState(0);
  const [requests, setRequests] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [actionKey, setActionKey] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const fetchSeqRef = useRef(0);

  useEffect(() => {
    const mSession = SessionManager.getCurrentSession();
    const sid = mSession?.[0]?.session_id || "";
    setSessionId(sid);
    setSessionName(mSession?.[0]?.session_name || "");
    setSessionStatus(mSession?.[0]?.status || "");
    setBootstrapped(true);
    window.scrollTo(0, 0);
  }, []);

  //Created By Cursor — mirrors Android SettlementRequestListActivity.getFeeSettlementRequest
  const fetchRequests = useCallback(
    async ({
      sid = sessionId,
      cid = classId,
      sec = sectionId,
      nextOffset = 0,
      append = false,
      soft = false,
    } = {}) => {
      if (!sid || !cid || !sec) {
        setRequests([]);
        setHasMore(false);
        return;
      }

      const seq = ++fetchSeqRef.current;
      if (append) setLoadingMore(true);
      else if (soft) setRefreshing(true);
      else setLoading(true);

      try {
        const SCHOOL_CODE = SessionManager.getSchoolCode();
        const response = await axios.get(
          `${API_BASE()}getFeeSettlementRequest.php`,
          {
            params: {
              school_code: SCHOOL_CODE,
              session_id: sid,
              class_id: cid,
              section_id: sec,
              limit: PAGE_LIMIT,
              offset: nextOffset,
            },
          }
        );

        if (seq !== fetchSeqRef.current) return;

        const data = response?.data;
        if (data?.api_status === "OK") {
          const list = Array.isArray(data.data) ? data.data : [];
          setRequests((prev) => (append ? [...prev, ...list] : list));
          setOffset(nextOffset);
          setHasMore(list.length >= PAGE_LIMIT);
          if (!append) setSelectedKeys(new Set());
        } else {
          if (!append) setRequests([]);
          setHasMore(false);
          if (data?.api_remark && data.api_remark !== "No Record found!!!") {
            myToast.notify_warning(data.api_remark);
          }
        }
      } catch (error) {
        if (seq !== fetchSeqRef.current) return;
        if (!append) setRequests([]);
        setHasMore(false);
        console.error("[ViewSettlement] getFeeSettlementRequest", error);
        myToast.notify_error("Failed to load settlement requests.");
      } finally {
        if (seq === fetchSeqRef.current) {
          setLoading(false);
          setLoadingMore(false);
          setRefreshing(false);
        }
      }
    },
    [sessionId, classId, sectionId]
  );

  //Created By Cursor
  const onSessionChange = useCallback((nextSessionId, nextSessionName, status) => {
    setSessionId(nextSessionId || "");
    setSessionName(nextSessionName || "");
    setSessionStatus(status || "");
    setClassId("");
    setSectionId("");
    setClassSectionName("");
    setSelectedClassPosition(0);
    setRequests([]);
    setHasMore(false);
    setOffset(0);
    setSelectedKeys(new Set());
  }, []);

  //Created By Cursor
  const onClassSelect = useCallback(
    (
      nextClassId,
      nextSectionId,
      nextSessionId,
      position,
      className,
      sectionName,
      nextSessionName
    ) => {
      const sid = nextSessionId || sessionId;
      const sec = nextSectionId || "No";
      setClassId(nextClassId);
      setSectionId(sec);
      setSessionId(sid);
      if (nextSessionName) setSessionName(nextSessionName);
      setSelectedClassPosition(position);
      setClassSectionName(
        sec === "No" ? className : `${className}:${sectionName}`
      );
      setSelectedKeys(new Set());
      fetchRequests({
        sid,
        cid: nextClassId,
        sec,
        nextOffset: 0,
        append: false,
      });
    },
    [fetchRequests, sessionId]
  );

  //Created By Cursor — hard delete (row removed from DB)
  const deleteSettlementRequest = useCallback(async (item) => {
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const staff = SessionManager.getStaffDetails();
    const requestId = String(item?.settlement_request_id || "").trim();
    if (!requestId) {
      return { api_status: "ERROR", api_remark: "Missing settlement request id" };
    }

    //Created By Cursor — prefer bulk hard-delete path (reliable by request_id)
    try {
      const bulk = await axios.post(
        `${API_BASE()}deleteSettlementRequestsBulk.php`,
        {
          school_code: SCHOOL_CODE,
          staff_id: staff?.staff_id,
          session_id: item.session_id || sessionId,
          class_id: item.class_id || classId,
          section_id: item.section_id || sectionId,
          request_ids: [requestId],
        }
      );
      if (bulk?.data?.api_status === "OK") {
        return bulk.data;
      }
    } catch (e) {
      // fall through to single endpoint
    }

    const response = await axios.post(
      `${API_BASE()}deleteSettlementRequest.php`,
      {
        school_code: SCHOOL_CODE,
        request_id: requestId,
        settlement_request_id: requestId,
        student_id: item.student_id,
        session_id: item.session_id || sessionId,
        class_id: item.class_id || classId,
        section_id: item.section_id || sectionId,
        staff_id: staff?.staff_id,
        fee_id: item.fee_id,
        applicable_month: item.applicable_month,
        amount: item.due_amount,
        due_amount: item.due_amount,
      },
      { headers: { "Content-Type": "application/json" } }
    );
    return response.data;
  }, [sessionId, classId, sectionId]);

  //Created By Cursor
  const handleDeleteOne = useCallback(
    async (item) => {
      if (
        !window.confirm(
          `Permanently delete settlement request for ₹${formatMoney(
            item.due_amount
          )}? This cannot be undone.`
        )
      ) {
        return;
      }
      const key = `${item.settlement_request_id}-delete`;
      setActionKey(key);
      try {
        const data = await deleteSettlementRequest(item);
        if (data?.api_status === "OK") {
          myToast.notify_success("Request deleted.");
          fetchRequests({
            nextOffset: 0,
            append: false,
            soft: true,
          });
        } else {
          myToast.notify_warning(
            data?.api_remark || "Delete failed. Please try again."
          );
        }
      } catch (error) {
        console.error("[ViewSettlement] delete one", error);
        myToast.notify_error("An error occurred while deleting the request.");
      } finally {
        setActionKey("");
      }
    },
    [deleteSettlementRequest, fetchRequests]
  );

  //Created By Cursor
  const toggleSelect = useCallback((rowKey) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(rowKey)) next.delete(rowKey);
      else next.add(rowKey);
      return next;
    });
  }, []);

  const filteredList = useMemo(() => {
    if (activeTab === "all") return requests;
    return requests.filter(
      (item) => String(item?.settlement_status) === String(activeTab)
    );
  }, [requests, activeTab]);

  const filteredKeys = useMemo(
    () => filteredList.map((item, index) => getRowKey(item, index)),
    [filteredList]
  );

  const allFilteredSelected =
    filteredKeys.length > 0 &&
    filteredKeys.every((key) => selectedKeys.has(key));

  const selectedCount = useMemo(() => {
    let n = 0;
    filteredKeys.forEach((key) => {
      if (selectedKeys.has(key)) n += 1;
    });
    return n;
  }, [filteredKeys, selectedKeys]);

  //Created By Cursor
  const toggleSelectAll = useCallback(() => {
    setSelectedKeys((prev) => {
      if (filteredKeys.length === 0) return new Set();
      const allOn = filteredKeys.every((key) => prev.has(key));
      if (allOn) return new Set();
      return new Set(filteredKeys);
    });
  }, [filteredKeys]);

  //Created By Cursor
  const handleDeleteSelected = useCallback(async () => {
    if (selectedCount === 0) return;
    if (
      !window.confirm(
        `Permanently delete ${selectedCount} selected settlement request${
          selectedCount === 1 ? "" : "s"
        }? This cannot be undone.`
      )
    ) {
      return;
    }

    const toDelete = filteredList.filter((item, index) =>
      selectedKeys.has(getRowKey(item, index))
    );
    if (!toDelete.length) return;

    const requestIds = [
      ...new Set(
        toDelete
          .map((item) => String(item?.settlement_request_id || "").trim())
          .filter(Boolean)
      ),
    ];
    if (!requestIds.length) {
      myToast.notify_warning("No valid request ids to delete.");
      return;
    }

    setBulkDeleting(true);
    try {
      const SCHOOL_CODE = SessionManager.getSchoolCode();
      const staff = SessionManager.getStaffDetails();
      const response = await axios.post(
        `${API_BASE()}deleteSettlementRequestsBulk.php`,
        {
          school_code: SCHOOL_CODE,
          staff_id: staff?.staff_id,
          session_id: sessionId,
          class_id: classId,
          section_id: sectionId,
          request_ids: requestIds,
        }
      );
      const data = response.data;
      if (data?.api_status === "OK") {
        const deleted = Number(data?.deleted) || requestIds.length;
        myToast.notify_success(
          `${deleted} request${deleted === 1 ? "" : "s"} deleted.`
        );
        setSelectedKeys(new Set());
        fetchRequests({
          nextOffset: 0,
          append: false,
          soft: true,
        });
      } else {
        myToast.notify_warning(
          data?.api_remark || "Delete failed. Please try again."
        );
      }
    } catch (error) {
      console.error("[ViewSettlement] bulk delete", error);
      myToast.notify_error("An error occurred while deleting requests.");
    } finally {
      setBulkDeleting(false);
    }
  }, [
    selectedCount,
    filteredList,
    selectedKeys,
    sessionId,
    classId,
    sectionId,
    fetchRequests,
  ]);

  const classSelected = Boolean(classId && sectionId && sessionId);

  const tabCounts = useMemo(() => {
    const counts = {
      all: requests.length,
      [STATUS.PENDING]: 0,
      [STATUS.SETTLED]: 0,
      [STATUS.REJECTED]: 0,
    };
    requests.forEach((item) => {
      const st = String(item?.settlement_status || "");
      if (counts[st] !== undefined) counts[st] += 1;
    });
    return counts;
  }, [requests]);

  //Created By Cursor
  const onTabChange = useCallback((value) => {
    setActiveTab(value);
    setSelectedKeys(new Set());
  }, []);

  if (!loggedIn) return <Redirect to="/Login" />;
  if (!bootstrapped) return <MyLoader />;

  const busy = !!actionKey || bulkDeleting;

  return (
    <div
      className="content payroll-page sm-page tr-page trep-page stl-page vsl-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Settlement Requests</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaListAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Settlement Request List</h1>
              <p className="sm-hero__sub">
                {classSectionName
                  ? `${classSectionName} · ${requests.length} request${
                      requests.length === 1 ? "" : "s"
                    }`
                  : "Select session & class to view requests"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              onClick={() =>
                fetchRequests({ nextOffset: 0, append: false, soft: true })
              }
              disabled={!classSelected || loading || refreshing || loadingMore}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
          </div>
        </header>

        <div className="tr-content trep-content">
          <div className="rn-class-wrap">
            <SelectSessionClass
              action={onClassSelect}
              onSessionChange={onSessionChange}
              selected_position={selectedClassPosition}
              session_id={sessionId}
              session_name={sessionName}
              session_status={sessionStatus}
              sessionChangePermission={true}
            />
          </div>

          {!classSelected ? (
            <div className="sm-empty vsl-empty">
              <div className="vsl-empty__orb" aria-hidden>
                <FaUserGraduate />
              </div>
              <p className="sm-empty__title">Select a class</p>
              <p className="sm-empty__hint">
                Choose session, class and section to load settlement requests
              </p>
            </div>
          ) : loading ? (
            <div className="tr-loading">
              <MyLoader />
            </div>
          ) : (
            <section className="trep-panel" aria-label="Settlement requests">
              <div className="trep-panel__head">
                <div className="min-w-0">
                  <h2 className="trep-panel__title">
                    <FaHandHoldingUsd aria-hidden /> Settlement
                  </h2>
                  <p className="trep-panel__sub">
                    Class-wise fee settlement requests
                  </p>
                </div>
                <div className="vsl-head-tools">
                  {filteredList.length > 0 ? (
                    <>
                      <button
                        type="button"
                        className="vsl-select-all-btn"
                        onClick={toggleSelectAll}
                        disabled={busy}
                      >
                        {allFilteredSelected ? (
                          <FaCheckSquare aria-hidden />
                        ) : (
                          <FaSquare aria-hidden />
                        )}
                        {allFilteredSelected ? "Clear" : "Select all"}
                      </button>
                      <button
                        type="button"
                        className="stl-btn stl-btn--danger vsl-bulk-delete"
                        disabled={busy || selectedCount === 0}
                        onClick={handleDeleteSelected}
                      >
                        <FaTrashAlt aria-hidden />
                        {bulkDeleting
                          ? "Deleting…"
                          : `Delete${selectedCount ? ` (${selectedCount})` : ""}`}
                      </button>
                    </>
                  ) : null}
                  <span className="trep-panel__count">
                    {filteredList.length} shown
                  </span>
                </div>
              </div>

              <div className="stl-body">
                <div
                  className="trep-chips"
                  role="tablist"
                  aria-label="Request status"
                >
                  {TABS.map((tab) => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.value;
                    return (
                      <button
                        key={tab.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        className={`trep-chip${active ? " is-active" : ""}`}
                        style={
                          active
                            ? {
                                borderColor: tab.color,
                                color: tab.color,
                                background: `${tab.color}12`,
                              }
                            : undefined
                        }
                        onClick={() => onTabChange(tab.value)}
                      >
                        <Icon aria-hidden />
                        <span>{tab.label}</span>
                        <span className="stl-tab-count">
                          {tabCounts[tab.value] || 0}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {filteredList.length === 0 ? (
                  <div className="sm-empty vsl-empty">
                    <div className="vsl-empty__orb vsl-empty__orb--muted" aria-hidden>
                      <FaListAlt />
                    </div>
                    <p className="sm-empty__title">No settlement request</p>
                    <p className="sm-empty__hint">
                      No requests found for this class
                      {activeTab !== "all"
                        ? ` (${statusLabel(activeTab)})`
                        : ""}
                      .
                    </p>
                  </div>
                ) : (
                  <div className="stl-list vsl-list">
                    {filteredList.map((item, index) => {
                      const student = parseStudentDetails(item.student_details);
                      const classInfo = Array.isArray(student.class)
                        ? student.class[0] || {}
                        : {};
                      const rowKey = getRowKey(item, index);
                      const isSelected = selectedKeys.has(rowKey);
                      const st = String(
                        item.settlement_status || STATUS.PENDING
                      );
                      const deleteBusy =
                        actionKey === `${item.settlement_request_id}-delete`;
                      const statusTone =
                        st === STATUS.SETTLED
                          ? "ok"
                          : st === STATUS.REJECTED
                          ? "bad"
                          : "warn";
                      const StatusIcon =
                        st === STATUS.SETTLED
                          ? FaCheck
                          : st === STATUS.REJECTED
                          ? FaTimes
                          : FaClock;
                      const classLabel = classInfo.class_name
                        ? `${classInfo.class_name}${
                            classInfo.section_name
                              ? `:${classInfo.section_name}`
                              : ""
                          }`
                        : classSectionName || "—";

                      return (
                        <article
                          key={rowKey}
                          role="button"
                          tabIndex={busy ? -1 : 0}
                          aria-pressed={isSelected}
                          className={`stl-card vsl-card vsl-card--${statusTone}${
                            isSelected ? " is-selected" : ""
                          }`}
                          onClick={() => {
                            if (!busy) toggleSelect(rowKey);
                          }}
                          onKeyDown={(e) => {
                            if (busy) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleSelect(rowKey);
                            }
                          }}
                        >
                          <div className="vsl-card__accent" aria-hidden />

                          <label
                            className="vsl-card__check"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={busy}
                              onChange={() => toggleSelect(rowKey)}
                            />
                            <span className="vsl-card__check-box" aria-hidden>
                              {isSelected ? <FaCheck /> : null}
                            </span>
                          </label>

                          <div className="stl-card__photo vsl-card__photo">
                            <StudentThumb
                              name={student.stu_name || "Student"}
                              imagePath={student.student_img_path}
                            />
                          </div>

                          <div className="stl-card__main vsl-card__main">
                            <div className="vsl-card__identity">
                              <h3 className="stl-card__name vsl-card__name">
                                {student.stu_name || "Student"}
                              </h3>
                              <p className="stl-card__relation vsl-card__relation">
                                <span className="vsl-card__relation-prefix">
                                  {studentRelation(student.gender)}
                                </span>{" "}
                                {student.father_name || "—"}
                              </p>
                            </div>

                            <div className="vsl-card__fee">
                              <div className="vsl-card__tags">
                                <span className="vsl-tag vsl-tag--roll">
                                  Roll {classInfo.student_roll_no || "—"}
                                </span>
                                <span className="vsl-tag vsl-tag--class">
                                  {classLabel}
                                </span>
                              </div>
                              <span className="vsl-card__fee-month">
                                <FaCalendarAlt aria-hidden />
                                {item.applicable_month || "—"}
                              </span>
                              <span className="vsl-card__fee-name">
                                {item.fee_name || "Fee"}
                              </span>
                            </div>
                          </div>

                          <div className="vsl-card__side">
                            <span
                              className={`stl-status stl-status--${statusTone} vsl-status`}
                            >
                              <StatusIcon aria-hidden />
                              {statusLabel(st)}
                            </span>

                            <div className="vsl-card__amount">
                              <span className="vsl-card__amount-value">
                                Due :₹{formatMoney(item.due_amount)}
                              </span>
                            </div>

                            <div className="stl-card__actions vsl-card__actions">
                              <button
                                type="button"
                                className="stl-btn stl-btn--danger vsl-remove-btn"
                                disabled={busy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteOne(item);
                                }}
                              >
                                <FaTrashAlt aria-hidden />
                                {deleteBusy ? "…" : "Delete"}
                              </button>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}

                {hasMore && activeTab === "all" ? (
                  <button
                    type="button"
                    className="stl-btn stl-btn--ghost stl-btn--block"
                    disabled={loadingMore}
                    onClick={() =>
                      fetchRequests({
                        nextOffset: offset + PAGE_LIMIT,
                        append: true,
                      })
                    }
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                ) : null}

                <div className="stl-footer">
                  <Link
                    to="/AdminMain/SettlementFeeAdvance"
                    className="stl-btn stl-btn--primary stl-btn--block"
                  >
                    Settle fee or due
                  </Link>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewSettlement;
